import { Link } from "react-router-dom";

const quickStartPaths = [
  { title: "Quick Start", description: "Get your API key and make your first request in 5 minutes", href: "/docs/getting-started/quickstart", icon: "fa-bolt" },
  { title: "SHL SDK", description: "Build and share encrypted health records with QR codes", href: "/docs/shl/overview", icon: "fa-qrcode", badge: "SDK" },
  { title: "MCP for AI", description: "Connect AI assistants to authoritative healthcare data", href: "/docs/mcp/overview", icon: "fa-robot" },
  { title: "Claims Intelligence", description: "Validate NCCI edits, MUE limits, and fee schedules", href: "/docs/claims/overview", icon: "fa-file-invoice-dollar" },
  { title: "Connectivity", description: "Find FHIR endpoints and Direct addresses by NPI", href: "/docs/connectivity/overview", icon: "fa-network-wired" },
  { title: "API Reference", description: "Interactive explorer with Try It functionality", href: "/tools/api-playground", icon: "fa-flask", badge: "Interactive" },
];

const clinicalApis = [
  { code: "NDC", name: "Drug Codes", href: "/docs/value-apis/ndc", icon: "fa-prescription-bottle" },
  { code: "NPI", name: "Providers", href: "/docs/value-apis/npi", icon: "fa-address-card" },
  { code: "RxNorm", name: "Drug Concepts", href: "/docs/value-apis/rxnorm", icon: "fa-capsules" },
  { code: "LOINC", name: "Lab Codes", href: "/docs/value-apis/loinc", icon: "fa-vial" },
  { code: "ICD-10", name: "Diagnoses", href: "/docs/value-apis/icd10", icon: "fa-file-medical" },
  { code: "CVX", name: "Vaccines", href: "/docs/value-apis/cvx", icon: "fa-syringe" },
  { code: "MVX", name: "Manufacturers", href: "/docs/value-apis/mvx", icon: "fa-industry" },
  { code: "FDA", name: "Drug Labels", href: "/docs/value-apis/fda-labels", icon: "fa-tag" },
  { code: "SNOMED CT", name: "Clinical Concepts", href: "/docs/value-apis/snomed", icon: "fa-dna" },
];

const claimsCards = [
  { title: "NCCI Edits", description: "Validate CPT/HCPCS code pairs", icon: "fa-code-compare" },
  { title: "MUE Limits", description: "Per-service quantity thresholds", icon: "fa-arrow-up-9-1" },
  { title: "Fee Schedule", description: "Medicare RVU & payment data", icon: "fa-money-check-dollar" },
  { title: "Coverage", description: "LCD determination lookups", icon: "fa-file-shield" },
];

const mcpLinks = [
  { title: "MCP Overview", href: "/docs/mcp/overview", icon: "fa-circle-info" },
  { title: "Claude Desktop Setup", href: "/docs/mcp/claude-desktop", icon: "fa-desktop" },
  { title: "HTTP API Reference", href: "/docs/mcp/api-reference", icon: "fa-code" },
];

const stats = [
  { number: "19", label: "Value APIs" },
  { number: "1M+", label: "Reference Records" },
  { number: "9.3M", label: "Provider Records" },
  { number: "4", label: "Published SDKs" },
];

export default function DocsLanding() {
  return (
    <div className="docs-landing">
      {/* Hero */}
      <section className="dl-hero">
        <div className="dl-eyebrow"><i className="fa-solid fa-book" />Documentation</div>
        <h1>FHIRfly Platform Documentation</h1>
        <p className="dl-sub">APIs, SDKs, and tools for healthcare data enrichment, patient health sharing, claims intelligence, and AI agent integration.</p>
        <div className="dl-ctas">
          <Link className="dl-btn primary" to="/docs/getting-started/quickstart"><i className="fa-solid fa-bolt" />Quick Start</Link>
          <Link className="dl-btn ghost" to="/tools/api-playground"><i className="fa-solid fa-flask" />API Reference</Link>
        </div>
      </section>

      {/* Quick start paths */}
      <section>
        <div className="dl-grid2">
          {quickStartPaths.map((card) => (
            <Link key={card.href} to={card.href} className="dl-card">
              <span className="ic"><i className={`fa-solid ${card.icon}`} /></span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="ct">{card.title}{card.badge && <span className="dl-badge">{card.badge}</span>}</span>
                <span className="cd">{card.description}</span>
              </span>
              <i className="fa-solid fa-arrow-right arrow" />
            </Link>
          ))}
        </div>
      </section>

      {/* Clinical data APIs */}
      <section>
        <h2><i className="fa-solid fa-database" />Clinical Data APIs</h2>
        <p className="dl-secsub">Instant access to 1M+ healthcare reference records through simple REST APIs.</p>
        <div className="dl-grid3">
          {clinicalApis.map((api) => (
            <Link key={api.code} to={api.href} className="dl-api">
              <span className="code"><i className={`fa-solid ${api.icon}`} />{api.code}</span>
              <span className="nm">{api.name}</span>
            </Link>
          ))}
        </div>
        <Link className="dl-more" to="/docs/value-apis/overview">View all APIs →</Link>
      </section>

      {/* SHL */}
      <section className="dl-panel leaf">
        <span className="dl-pillbadge"><i className="fa-solid fa-link" />SMART Health Links</span>
        <h2 style={{ margin: "0 0 8px" }}>Build shareable health records</h2>
        <p className="dl-secsub" style={{ maxWidth: 640 }}>
          The <code className="docs-code-inline">@fhirfly-io/shl</code> SDK builds IPS bundles, encrypts them with AES-256-GCM, and shares via QR code — all in under 20 lines of code.
        </p>
        <ul className="dl-feat">
          <li><i className="fa-solid fa-lock" style={{ color: "var(--ffly-leaf)" }} /><span><strong>Zero-knowledge encryption</strong> — server never sees the key</span></li>
          <li><i className="fa-solid fa-shield-halved" style={{ color: "var(--ffly-leaf)" }} /><span><strong>PSHD compliance built in</strong> — CMS-aligned direct retrieval</span></li>
          <li><i className="fa-solid fa-code" style={{ color: "var(--ffly-leaf)" }} /><span><strong>IPS Bundle builder</strong> — auto-resolves NDC, ICD-10, LOINC, CVX codes</span></li>
        </ul>
        <div className="dl-ctas">
          <Link className="dl-btn primary" to="/docs/shl/overview"><i className="fa-solid fa-book-open" />SHL Overview</Link>
          <Link className="dl-btn ghost" to="/docs/shl/quickstart"><i className="fa-solid fa-bolt" />Quick Start</Link>
        </div>
      </section>

      {/* Claims */}
      <section className="dl-panel sun">
        <h2 style={{ margin: "0 0 6px" }}>Claims Intelligence</h2>
        <p className="dl-secsub">Pre-submission claims validation powered by CMS data.</p>
        <div className="dl-grid2" style={{ marginBottom: 16 }}>
          {claimsCards.map((card) => (
            <div key={card.title} className="dl-claimcard">
              <i className={`fa-solid ${card.icon}`} />
              <div><div className="t">{card.title}</div><div className="d">{card.description}</div></div>
            </div>
          ))}
        </div>
        <Link className="dl-more" to="/docs/claims/overview">Explore Claims Intelligence →</Link>
      </section>

      {/* More capabilities */}
      <section>
        <div className="dl-grid2">
          <Link to="/docs/search-apis/overview" className="dl-card">
            <span className="ic"><i className="fa-solid fa-magnifying-glass" /></span>
            <span style={{ flex: 1 }}><span className="ct">Search APIs</span><span className="cd">Full-text search across all clinical data with filters, facets, and highlighting.</span></span>
          </Link>
          <Link to="/docs/connectivity/overview" className="dl-card">
            <span className="ic"><i className="fa-solid fa-network-wired" /></span>
            <span style={{ flex: 1 }}><span className="ct">Connectivity</span><span className="cd">Discover FHIR endpoints, Direct addresses, and EHR connectivity by NPI.</span></span>
          </Link>
        </div>
      </section>

      {/* MCP */}
      <section className="dl-panel blue">
        <h2 style={{ margin: "0 0 6px" }}>MCP Integration</h2>
        <p className="dl-secsub" style={{ maxWidth: 640 }}>Give AI agents access to authoritative healthcare data instead of hallucinated answers, via the Model Context Protocol.</p>
        {mcpLinks.map((item) => (
          <Link key={item.href} to={item.href} className="dl-mcplink">
            <i className={`fa-solid ${item.icon}`} />
            <span className="t">{item.title}</span>
            <i className="fa-solid fa-arrow-right arrow" />
          </Link>
        ))}
      </section>

      {/* Stats */}
      <section>
        <div className="dl-stats">
          {stats.map((stat) => (
            <div key={stat.label}><div className="n">{stat.number}</div><div className="l">{stat.label}</div></div>
          ))}
        </div>
      </section>
    </div>
  );
}
